import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import {
  getPendingAutoevaluations,
  addBatchAutoevaluation,
} from "../../components/funtionsBD/Mainservice";
import { AddHeader } from "../../components/AddHeader";
import "./PlayerAutoevaluation.css";

const PERF_LABELS = {
  en: ["Poor", "Below avg", "Average", "Good", "Excellent"],
  fr: ["Faible", "Sous moy.", "Moyen", "Bon", "Excellent"],
  de: ["Schlecht", "Unter Avg", "Mittel", "Gut", "Exzellent"],
  es: ["Malo", "Bajo prom.", "Promedio", "Bueno", "Excelente"],
  nl: ["Zwak", "Onder gem.", "Gemiddeld", "Goed", "Uitstekend"],
  pt: ["Fraco", "Abaixo da média", "Médio", "Bom", "Excelente"],
};

const EXERTION_LABELS = {
  en: ["Very light", "Light", "Moderate", "Hard", "Maximum"],
  fr: ["Très léger", "Léger", "Modéré", "Intense", "Maximum"],
  de: ["Sehr leicht", "Leicht", "Mittel", "Schwer", "Maximum"],
  es: ["Muy ligero", "Ligero", "Moderado", "Intenso", "Máximo"],
  nl: ["Zeer licht", "Licht", "Gemiddeld", "Zwaar", "Maximaal"],
  pt: ["Muito leve", "Leve", "Moderado", "Intenso", "Máximo"],
};

const PERF_COLORS = ["#ef4444", "#f97316", "#eab308", "#22c55e", "#10b981"];
const EXERTION_COLORS = ["#22c55e", "#84cc16", "#eab308", "#f97316", "#ef4444"];

const PERF_ICONS = ["😞", "😕", "😐", "🙂", "🤩"];
const EXERTION_ICONS = ["💤", "🚶", "🏃", "🔥", "💥"];

function normalizeAppLang(lng) {
  const base = (lng || "en").split("-")[0];
  if (base === "gr") return "de";
  return base;
}

const getLocalized = (obj, prefix, lang) => {
  if (!obj) return "";
  return (
    obj[`${prefix}_${lang}`] ||
    obj[`${prefix}_en`] ||
    obj[`${prefix}_fr`] ||
    obj[`${prefix}_de`] ||
    obj[`${prefix}_es`] ||
    ""
  );
};

const RatingSelector = ({ value, onChange, colors, icons, labels }) => {
  return (
    <div className="ae-rating-row">
      {[1, 2, 3, 4, 5].map((level) => {
        const isActive = value === level;
        const color = colors[level - 1];
        return (
          <button
            key={level}
            type="button"
            className={`ae-rating-btn ${isActive ? "ae-rating-btn--active" : ""}`}
            style={
              isActive
                ? { backgroundColor: color, borderColor: color, color: "#fff" }
                : { borderColor: color + "66" }
            }
            onClick={() => onChange(level)}
          >
            <span className="ae-rating-btn__icon">{icons[level - 1]}</span>
            <span className="ae-rating-btn__num">{level}</span>
            <span className="ae-rating-btn__label">{labels[level - 1]}</span>
          </button>
        );
      })}
    </div>
  );
};

const ExerciseEvalCard = ({ exercise, evaluation, onUpdate, lang, t, priority }) => {
  const exName =
    getLocalized(exercise.exercise, "ex_name", lang) || exercise.exercise?.ex_name || "";
  const typeLabel =
    getLocalized(exercise.competency_block, "cb_name", lang) ||
    getLocalized(exercise.sub_type_details, "sub_type_name", lang) ||
    "";
  const objective = getLocalized(exercise.exercise, "ex_objective", lang) || "";
  const perfLabels = PERF_LABELS[lang] || PERF_LABELS.en;
  const exertionLabels = EXERTION_LABELS[lang] || EXERTION_LABELS.en;
  const isComplete = evaluation.exercise_perf && evaluation.perceived_exertion;

  return (
    <div className={`ae-exercise-card ${isComplete ? "ae-exercise-card--complete" : ""} ${priority ? "ae-exercise-card--priority" : "ae-exercise-card--optional"}`}>
      <div className="ae-exercise-card__header">
        <div className="ae-exercise-card__info">
          <h4 className="ae-exercise-card__name">{exName}</h4>
          <div className="ae-exercise-card__badges">
            <span className={`ae-exercise-card__focus ${priority ? "ae-exercise-card__focus--priority" : "ae-exercise-card__focus--optional"}`}>
              {priority ? t("ae_badge_priority", "Priority") : t("ae_badge_optional", "Optional")}
            </span>
            {typeLabel && (
              <span className="ae-exercise-card__cb-badge">{typeLabel}</span>
            )}
          </div>
        </div>
        {exercise.te_duration && (
          <span className="ae-exercise-card__duration">
            {exercise.te_duration} {t("min")}
          </span>
        )}
        {isComplete && <span className="ae-exercise-card__check">&#10003;</span>}
      </div>

      {objective && (
        <p className="ae-exercise-card__objective">{objective}</p>
      )}

      <div className="ae-exercise-card__rating-section">
        <div className="ae-exercise-card__rating-group">
          <label className="ae-exercise-card__rating-label">
            <span className="ae-rating-label-icon">🎯</span>
            {t("ae_performance")}
          </label>
          <RatingSelector
            value={evaluation.exercise_perf}
            onChange={(val) => onUpdate(exercise.te_id, "exercise_perf", val)}
            colors={PERF_COLORS}
            icons={PERF_ICONS}
            labels={perfLabels}
          />
        </div>

        <div className="ae-exercise-card__rating-group">
          <label className="ae-exercise-card__rating-label">
            <span className="ae-rating-label-icon">💪</span>
            {t("ae_exertion")}
          </label>
          <RatingSelector
            value={evaluation.perceived_exertion}
            onChange={(val) => onUpdate(exercise.te_id, "perceived_exertion", val)}
            colors={EXERTION_COLORS}
            icons={EXERTION_ICONS}
            labels={exertionLabels}
          />
        </div>
      </div>
    </div>
  );
};

export const PlayerAutoevaluation = () => {
  const [sessions, setSessions] = useState([]);
  const [evaluations, setEvaluations] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [totalPending, setTotalPending] = useState(0);
  const [requiredPending, setRequiredPending] = useState(0);
  const [emptyReason, setEmptyReason] = useState(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [appLang, setAppLang] = useState(() =>
    normalizeAppLang(i18n.language || localStorage.getItem("i18nextLng"))
  );

  useEffect(() => {
    const onLang = (lng) => setAppLang(normalizeAppLang(lng));
    i18n.on("languageChanged", onLang);
    return () => i18n.off("languageChanged", onLang);
  }, []);

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getPendingAutoevaluations();
      const data = response.data.data || [];
      const ordered = data.map((session) => ({
        ...session,
        exercises: [...(session.exercises || [])].sort((a, b) => {
          const rank = (item) => (item.eval_focus === "optional" ? 1 : 0);
          return rank(a) - rank(b);
        }),
      }));
      setSessions(ordered);
      const requiredFromApi = response.data.required_pending;
      const requiredCount =
        requiredFromApi != null
          ? Number(requiredFromApi) || 0
          : ordered.reduce(
              (sum, session) =>
                sum + session.exercises.filter((ex) => ex.eval_focus !== "optional").length,
              0
            );
      setRequiredPending(requiredCount);
      setTotalPending(response.data.total_pending || 0);
      setEmptyReason(ordered.length === 0 ? response.data.empty_reason || null : null);

      const initEvals = {};
      ordered.forEach((session) => {
        session.exercises.forEach((ex) => {
          initEvals[ex.te_id] = { exercise_perf: null, perceived_exertion: null };
        });
      });
      setEvaluations(initEvals);
    } catch (error) {
      console.error("Error fetching pending autoevaluations:", error);
      setSnack({ open: true, message: error.message || "Failed to load", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const handleUpdate = (te_id, field, value) => {
    setEvaluations((prev) => ({
      ...prev,
      [te_id]: { ...prev[te_id], [field]: value },
    }));
  };

  const getCompletedEvaluations = () => {
    return Object.entries(evaluations)
      .filter(([, val]) => val.exercise_perf && val.perceived_exertion)
      .map(([te_id, val]) => ({
        te_id,
        exercise_perf: String(val.exercise_perf),
        perceived_exertion: String(val.perceived_exertion),
      }));
  };

  const completedCount = getCompletedEvaluations().length;
  const requiredIds = new Set(
    sessions.flatMap((session) =>
      (session.exercises || [])
        .filter((ex) => ex.eval_focus !== "optional")
        .map((ex) => ex.te_id)
    )
  );
  const requiredCompleted = getCompletedEvaluations().filter((item) =>
    requiredIds.has(item.te_id)
  ).length;
  const progressTotal = requiredPending > 0 ? requiredPending : totalPending;
  const progressDone = requiredPending > 0 ? requiredCompleted : completedCount;

  const handleSubmitAll = async () => {
    const completed = getCompletedEvaluations();
    if (completed.length === 0) {
      setSnack({ open: true, message: t("ae_select_ratings"), severity: "warning" });
      return;
    }

    setSubmitting(true);
    try {
      const response = await addBatchAutoevaluation({ evaluations: completed });
      setSnack({
        open: true,
        message: response.data.message || t("ae_success"),
        severity: "success",
      });
      await fetchPending();
    } catch (error) {
      setSnack({
        open: true,
        message: error.message || "Submission failed",
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitSession = async (session) => {
    const sessionEvals = session.exercises
      .map((ex) => ({ te_id: ex.te_id, ...evaluations[ex.te_id] }))
      .filter((e) => e.exercise_perf && e.perceived_exertion)
      .map((e) => ({
        te_id: e.te_id,
        exercise_perf: String(e.exercise_perf),
        perceived_exertion: String(e.perceived_exertion),
      }));

    if (sessionEvals.length === 0) {
      setSnack({ open: true, message: t("ae_select_ratings"), severity: "warning" });
      return;
    }

    setSubmitting(true);
    try {
      const response = await addBatchAutoevaluation({ evaluations: sessionEvals });
      setSnack({
        open: true,
        message: response.data.message || t("ae_success"),
        severity: "success",
      });
      await fetchPending();
    } catch (error) {
      setSnack({
        open: true,
        message: error.message || "Submission failed",
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString(
      appLang === "fr" ? "fr-FR" : appLang === "de" ? "de-DE" : appLang === "es" ? "es-ES" : "en-US",
      {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    }
    );
  };

  const getSessionCompletedCount = (session) => {
    return session.exercises.filter((ex) => {
      const ev = evaluations[ex.te_id];
      return ev?.exercise_perf && ev?.perceived_exertion;
    }).length;
  };

  if (loading) {
    return (
      <>
        <AddHeader title={t("ae_title")} />
        <div className="ae-loading">
          <CircularProgress size={48} sx={{ color: "#0877BC" }} />
          <p>{t("loading") || "Loading..."}</p>
        </div>
      </>
    );
  }

  if (sessions.length === 0) {
    const isNewPlayer = emptyReason !== "all_evaluated";
    return (
      <>
        <AddHeader title={t("ae_title")} />
        <div className={`ae-empty ${isNewPlayer ? "ae-empty--welcome" : ""}`}>
          <div className="ae-empty__icon">{isNewPlayer ? "🎯" : "🏆"}</div>
          <h3>{t(isNewPlayer ? "ae_welcome_title" : "ae_all_done_title")}</h3>
          <p>{t(isNewPlayer ? "ae_welcome_msg" : "ae_all_done_msg")}</p>
          <button className="ae-btn ae-btn--primary" onClick={() => navigate("/home")}>
            {t("ae_go_home")}
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <AddHeader title={t("ae_title")} />

      <div className="ae-container">
        <div className="ae-hero">
          <div className="ae-hero__content">
            <h2 className="ae-hero__title">{t("ae_hero_title")}</h2>
            <p className="ae-hero__subtitle">
              {requiredPending > 0
                ? t("ae_hero_priority", {
                    count: requiredPending,
                    defaultValue:
                      "{{count}} personalized exercise(s) from this cycle still need your rating.",
                  })
                : t("ae_hero_optional_only", {
                    defaultValue:
                      "Personalized exercises for this cycle are done. Other competency blocks below are optional.",
                  })}
            </p>
          </div>
          <div className="ae-hero__progress">
            <div className="ae-hero__progress-ring">
              <svg viewBox="0 0 80 80">
                <circle cx="40" cy="40" r="34" className="ae-progress-bg" />
                <circle
                  cx="40"
                  cy="40"
                  r="34"
                  className="ae-progress-fill"
                  style={{
                    strokeDasharray: `${2 * Math.PI * 34}`,
                    strokeDashoffset: `${2 * Math.PI * 34 * (1 - (progressTotal > 0 ? progressDone / progressTotal : 0))}`,
                  }}
                />
              </svg>
              <span className="ae-hero__progress-text">
                {progressDone}/{progressTotal}
              </span>
            </div>
          </div>
        </div>

        <div className="ae-sessions">
          {sessions.map((session) => {
            const sessionCompleted = getSessionCompletedCount(session);
            const sessionTotal = session.exercises.length;
            return (
              <div key={session.ts_id} className="ae-session">
                <div className="ae-session__header">
                  <div className="ae-session__info">
                    <span className="ae-session__date">{formatDate(session.ts_date)}</span>
                    {session.gr_name && (
                      <span className="ae-session__group">{session.gr_name}</span>
                    )}
                  </div>
                  <div className="ae-session__meta">
                    <span className="ae-session__badge">
                      {sessionCompleted}/{sessionTotal} {t("ae_rated")}
                    </span>
                    {session.ts_duration && (
                      <span className="ae-session__duration">
                        {session.ts_duration} {t("min")}
                      </span>
                    )}
                  </div>
                </div>

                <div className="ae-session__exercises">
                  {session.exercises.map((exercise) => (
                    <ExerciseEvalCard
                      key={exercise.te_id}
                      exercise={exercise}
                      evaluation={evaluations[exercise.te_id] || {}}
                      onUpdate={handleUpdate}
                      lang={appLang}
                      t={t}
                      priority={exercise.eval_focus !== "optional"}
                    />
                  ))}
                </div>

                {sessionTotal > 0 && (
                  <button
                    className={`ae-btn ae-btn--session ${sessionCompleted === 0 ? "ae-btn--disabled" : ""}`}
                    onClick={() => handleSubmitSession(session)}
                    disabled={submitting || sessionCompleted === 0}
                  >
                    {submitting ? (
                      <CircularProgress size={20} sx={{ color: "#fff" }} />
                    ) : (
                      <>
                        {t("ae_submit_session")} ({sessionCompleted})
                      </>
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {completedCount > 0 && (
          <div className="ae-footer">
            <button
              className="ae-btn ae-btn--primary ae-btn--large"
              onClick={handleSubmitAll}
              disabled={submitting}
            >
              {submitting ? (
                <CircularProgress size={24} sx={{ color: "#fff" }} />
              ) : (
                <>
                  {t("ae_submit_all")} ({completedCount})
                </>
              )}
            </button>
          </div>
        )}
      </div>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert variant="filled" severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
};
